// Workaround for MPEG-TS live streams whose segments don't start with a keyframe
// (e.g. 2 s segments with a 6 s GOP).
//
// hls.js creates the MediaSource buffers from the first fragment it parses. If that fragment has
// audio but no H.264 SPS, only an audio buffer is created and the next fragment with video fails
// with a fatal "bufferAppendError" (video SourceBuffer does not exist). Until a fragment with an
// SPS has been seen, this loader empties fragments without one (all packets except PAT/PMT go to
// the null PID); hls.js skips them as "no media" and starts from the first fragment with video.
// Other streams (fMP4, HEVC, audio only, or TS that starts with an SPS) pass through unchanged.

const PACKET = 188;
const NULL_PID = 0x1fff;

function packet_pid(u8, o) {
	return ((u8[o + 1] & 0x1f) << 8) | u8[o + 2];
}

function payload_start(u8, o) {
	return u8[o + 3] & 0x20 ? o + 5 + u8[o + 4] : o + 4;
}

// PMT PID and the first H.264 video PID
function ts_info(u8) {
	var pmt = -1;
	var video = -1;
	for (var o = 0; o + PACKET <= u8.length && video < 0; o += PACKET) {
		if (u8[o] !== 0x47 || !(u8[o + 1] & 0x40)) continue;
		var pid = packet_pid(u8, o);
		var p = payload_start(u8, o);
		p += u8[p] + 1;
		if (pid === 0 && pmt < 0) {
			pmt = packet_pid(u8, p + 9);
		} else if (pid === pmt) {
			var end = p + 3 + (((u8[p + 1] & 0x0f) << 8) | u8[p + 2]) - 4;
			var q = p + 12 + (((u8[p + 10] & 0x0f) << 8) | u8[p + 11]);
			while (q + 5 <= end) {
				if (u8[q] === 0x1b) {
					video = packet_pid(u8, q);
					break;
				}
				q += 5 + (((u8[q + 3] & 0x0f) << 8) | u8[q + 4]);
			}
		}
	}
	return {pmt: pmt, video: video};
}

// Looks for a NAL start code followed by an SPS (NAL type 7) in the video payloads
function has_sps(u8, video_pid) {
	var zeros = 0;
	for (var o = 0; o + PACKET <= u8.length; o += PACKET) {
		if (u8[o] !== 0x47 || packet_pid(u8, o) !== video_pid) continue;
		var end = o + PACKET;
		for (var p = payload_start(u8, o); p < end; p++) {
			var b = u8[p];
			if (zeros >= 2 && b === 1) {
				if (p + 1 < end && (u8[p + 1] & 0x1f) === 7) return true;
				zeros = 0;
			} else {
				zeros = b === 0 ? zeros + 1 : 0;
			}
		}
	}
	return false;
}

function empty_fragment(u8, pmt_pid) {
	for (var o = 0; o + PACKET <= u8.length; o += PACKET) {
		var pid = packet_pid(u8, o);
		if (u8[o] !== 0x47 || pid === 0 || pid === pmt_pid) continue;
		u8[o + 1] = (u8[o + 1] & 0xe0) | (NULL_PID >> 8);
		u8[o + 2] = NULL_PID & 0xff;
	}
}

// Fragment loader class for one Hls instance (config.fLoader).
// on_fragment(ArrayBuffer, frag) gets every loaded fragment before hls.js (which detaches the buffer)
export function createKeyframeStartLoader(Hls, on_fragment) {
	var Base = Hls.DefaultConfig.loader;
	var started = false;
	return class KeyframeStartLoader extends Base {
		load(context, config, callbacks) {
			var on_success = callbacks.onSuccess;
			var wrapped = Object.assign({}, callbacks, {
				onSuccess: (response, stats, ctx, details) => {
					var data = response && response.data instanceof ArrayBuffer ? response.data : null;
					if (data && on_fragment) {
						try {
							on_fragment(data, ctx.frag);
						} catch (err) {
							console.warn("[KeyframeStartLoader] fragment hook failed", err);
						}
					}
					if (!started && data) {
						var u8 = new Uint8Array(data);
						var info = ts_info(u8);
						if (info.video < 0 || has_sps(u8, info.video)) started = true;
						else empty_fragment(u8, info.pmt);
					}
					on_success(response, stats, ctx, details);
				}
			});
			super.load(context, config, wrapped);
		}
	};
}
